/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC } from 'react';
import { Helmet } from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';
import { useLocation } from '@reach/router';

export interface metaDescriptionProp {
  data: { metaDescription: String };
}

interface SeoProps {
  description?: metaDescriptionProp;
  lang?: string;
  title: string;
  author?: string;
  image?: any;
}

const Seo = ({
  description,
  lang = 'en',
  title = 'Sacred Heart Church',
  author = '',
  image = '',
}: SeoProps) => {
  const { pathname } = useLocation();
  return (
    <StaticQuery
      query={seoDetailsQuery}
      render={data => {
        const {
          site: {
            siteMetadata: {
              defaultTitle,
              siteUrl,
              isDevEnvironment,
              defaultAuthor,
            },
          },
          strapiGlobal: {
            defaultSeo: {
              metaTitle: defaultMetaTitle,
              metaDescription: defaultMetaDescription,
              shareImage: shareImageURL,
            },
          },
        } = data;

        const seoTitle = title || defaultMetaTitle;
        const seoDescription =
          description?.data?.metaDescription ||
          defaultMetaDescription?.data?.metaDescription;
        const metaUrl = `${siteUrl}${pathname}`;
        const metaImage =
          image ||
          (shareImageURL?.localFile?.publicURL &&
            `${siteUrl}${shareImageURL?.localFile?.publicURL}`);
        const canonical = pathname ? `${siteUrl}${pathname}` : null;
        const isDev = isDevEnvironment;
        const Author = author || defaultAuthor;
        return (
          <Helmet
            htmlAttributes={{
              lang,
            }}
            title={seoTitle}
            titleTemplate={`%s | ${defaultTitle}`}
            link={
              canonical
                ? [
                    {
                      rel: 'canonical',
                      href: canonical,
                    },
                  ]
                : []
            }
            meta={[
              {
                name: 'description',
                content: seoDescription,
              },
              {
                name: 'image',
                content: metaImage,
              },
              {
                name: 'og:image:width',
                content: 200,
              },
              {
                name: 'og:image:height',
                content: 150,
              },
              {
                property: 'og:title',
                content: seoTitle,
              },
              {
                property: `og:url`,
                content: metaUrl,
              },
              {
                property: 'og:description',
                content: seoDescription,
              },
              {
                property: 'og:type',
                content: 'website',
              },
              {
                name: 'twitter:card',
                content: 'summary',
              },
              {
                name: 'twitter:creator',
                content: Author,
              },
              {
                name: 'twitter:title',
                content: seoTitle,
              },
              {
                name: 'twitter:description',
                content: seoDescription,
              },
              {
                name: 'twitter:image',
                content: metaImage,
              },
            ]
              .concat(
                metaImage
                  ? [
                      {
                        property: `og:image`,
                        content: metaImage,
                      },
                      {
                        property: `og:image:alt`,
                        content: seoTitle,
                      },
                    ]
                  : []
              )
              .concat(
                isDev
                  ? [
                      {
                        property: `robots`,
                        content: `noindex`,
                      },
                    ]
                  : []
              )}
          />
        );
      }}
    />
  );
};

export { Seo };

const seoDetailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        defaultTitle: title
        defaultDescription: description
        siteUrl
        defaultAuthor: author
        isDevEnvironment
        defaultImage: image
      }
    }
    strapiGlobal {
      defaultSeo {
        metaTitle
        metaDescription {
          data {
            metaDescription
          }
        }
        shareImage {
          localFile {
            publicURL
          }
        }
      }
    }
  }
`;
